/**
 * Subtle card interactions (CSS in dvy-home.css, gated on html.dvz-i so content shows without JS).
 * Reveal-once in view, timeline progress line, stat count-up, one-open accordion, plant tilt/toggle.
 * prefers-reduced-motion: everything is shown at rest; no tilt, no counting, no progress tracking.
 */
(() => {
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  root.classList.add('dvz-i');

  const once = (els, fn, threshold = 0.25) => {
    if (!els.length) return;
    if (reduced || !('IntersectionObserver' in window)) return els.forEach(fn);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          io.unobserve(e.target);
          fn(e.target);
        }
      });
    }, { threshold });
    els.forEach((el) => io.observe(el));
  };
  const $$ = (sel) => [...document.querySelectorAll(sel)];

  // Timeline rows + comparison checks
  once($$('.dvz-rise'), (el) => el.classList.add('is-in'), 0.2);
  once($$('[data-dvz-inview]'), (el) => el.classList.add('is-in'), 0.3);

  // Timeline progress line: fills as the section scrolls past the middle of the viewport
  const lines = $$('[data-dvz-progress]');
  if (lines.length && !reduced) {
    let queued = false;
    const update = () => {
      queued = false;
      const mid = innerHeight * 0.6;
      lines.forEach((el) => {
        const r = el.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, (mid - r.top) / (r.height || 1)));
        el.style.setProperty('--p', p.toFixed(3));
      });
    };
    const req = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    addEventListener('scroll', req, { passive: true });
    addEventListener('resize', req, { passive: true });
    update();
  }

  // Stat band: count up once. Width is locked to the final value so nothing shifts.
  $$('[data-dvz-count] .dvz-num').forEach((num) => {
    const end = parseInt(num.textContent, 10);
    if (reduced || Number.isNaN(end) || String(end) !== num.textContent.trim()) return;
    if (end === 0) return num.classList.add('dvz-num--fade');
    num.style.width = `${num.getBoundingClientRect().width}px`;
    num.textContent = '0';
    once([num], () => {
      const start = performance.now();
      const dur = end > 1 ? 900 : 400;
      const tick = (t) => {
        const k = Math.min(1, (t - start) / dur);
        num.textContent = Math.round(end * (1 - Math.pow(1 - k, 3)));
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, 0.6);
  });
  once($$('.dvz-num--fade'), (el) => el.classList.add('is-in'), 0.6);

  // Benefits: one open at a time, animated height (grid rows) on open and close
  $$('[data-dvz-accordion]').forEach((list) => {
    const items = [...list.querySelectorAll('details')];
    const close = (d) => {
      d.classList.remove('is-open');
      if (reduced) return (d.open = false);
      const body = d.querySelector('.dvz-benefit__body');
      const done = () => { if (!d.classList.contains('is-open')) d.open = false; };
      body.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
    };
    items.forEach((d) => {
      if (d.open) d.classList.add('is-open');
      d.querySelector('summary').addEventListener('click', (ev) => {
        ev.preventDefault();
        if (d.classList.contains('is-open')) return close(d);
        items.forEach((o) => o !== d && o.classList.contains('is-open') && close(o));
        d.open = true;
        requestAnimationFrame(() => requestAnimationFrame(() => d.classList.add('is-open')));
      });
    });
  });

  // Sticky scroll link (phones): shown unless the target section or the footer is on screen; never adds to cart
  const bar = document.querySelector('[data-dvz-sticky-link]');
  const goal = bar && document.getElementById(bar.dataset.target);
  if (bar && goal) {
    const foot = document.querySelector('footer');
    let raf = 0;
    const sync = () => {
      raf = 0;
      const g = goal.getBoundingClientRect();
      const show = !(g.top < innerHeight && g.bottom > 0) && !(foot && foot.getBoundingClientRect().top < innerHeight);
      bar.toggleAttribute('data-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelector('a').tabIndex = show ? 0 : -1;
    };
    bar.hidden = false;
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(sync); }, { passive: true, capture: true });
    bar.querySelector('a').addEventListener('click', (e) => {
      e.preventDefault();
      goal.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    });
    sync();
  }

  // Three plants: toggle (touch) + tilt toward the pointer (desktop, rAF-throttled)
  $$('[data-dvz-herb]').forEach((herb) => {
    const btn = herb.querySelector('.dvyh-herb__more');
    if (btn) {
      herb.classList.add('dvz-herb');
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', open);
        herb.classList.toggle('is-open', open);
      });
    }
    const card = herb.querySelector('[data-dvz-tilt]');
    if (!card || reduced) return;
    let frame = 0;
    herb.addEventListener('pointermove', (e) => {
      if (!fine.matches || e.pointerType !== 'mouse' || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(900px) rotateX(${(-y * 8).toFixed(2)}deg) rotateY(${(x * 8).toFixed(2)}deg)`;
      });
    });
    herb.addEventListener('pointerleave', () => {
      cancelAnimationFrame(frame);
      frame = 0;
      card.style.transform = '';
    });
  });
})();
